import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

/* ================= TYPES ================= */
interface PaymentRaw {
  id: number;
  montant: number;
  mode: string;
  date_paiement: string;
  reference: string | null;
  date_validation?: string | null;
  deleted_at?: string | null;
  facture_principale?: {
    numero: string;
    bon_livraison?: {
      numero: string;
    };
  };
  client?: {
    raison_sociale: string;
    email: string;
  };
}

interface Payment {
  id: string;
  invoiceNumber: string;
  supplierInvoiceNumber: string;
  supplier: string;
  amount: number;
  currency: string;
  date: string;
  paymentMethod: string;
  reference: string;
  status: 'Validé' | 'En Attente' | 'Annulé';
}

/* ================= COMPONENT ================= */
export default function PaymentsList() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('Tous');
  const [statusFilter, setStatusFilter] = useState('Tous');

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const res = await getResources(`factures-client/paiements-clients`);
        const rawPayments = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
            ? res.data.data
            : [];

        const mapped: Payment[] = rawPayments.map((p: PaymentRaw) => ({
          id: String(p.id),
          invoiceNumber: p.facture_principale?.numero ?? '—',
          supplierInvoiceNumber: p.facture_principale?.bon_livraison?.numero ?? '—',
          supplier: p.client?.raison_sociale ?? '—',
          amount: Number(p.montant),
          currency: 'GNF',
          date: p.date_paiement,
          paymentMethod: mapMethod(p.mode),
          reference: p.reference || '—',
          status: mapStatus(p),
        }));

        setPayments(mapped);
      } catch (e) {
        console.error(e);
        toast.error('Erreur lors du chargement des règlements');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  /* ================= HELPERS ================= */
  const mapStatus = (p: PaymentRaw): Payment['status'] => {
    if (p.deleted_at) return 'Annulé';
    if (p.date_validation) return 'Validé';
    return 'En Attente';
  };

  const mapMethod = (mode: string) => {
    const map: Record<string, string> = {
      VIREMENT: 'Virement',
      CHEQUE: 'Chèque',
      ESPECES: 'Espèces',
      CARTE: 'Carte',
      MOBILE_MONEY: 'Mobile Money',
      COMPENSATION: 'Compensation',
    };
    return map[mode] ?? mode;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';

  const getMethodIcon = (method: string) => {
    const icons: Record<string, string> = {
      Virement: 'ri-bank-line',
      Chèque: 'ri-file-paper-line',
      Espèces: 'ri-money-dollar-circle-line',
      Carte: 'ri-bank-card-line',
      'Mobile Money': 'ri-smartphone-line',
      Compensation: 'ri-exchange-line',
    };
    return icons[method] ?? 'ri-money-dollar-circle-line';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      Validé: 'bg-green-100 text-green-800 border-green-200',
      'En Attente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Annulé: 'bg-red-100 text-red-800 border-red-200',
    };
    return badges[status] ?? 'bg-gray-100 text-gray-800 border-gray-200';
  };

  /* ================= FILTRES ================= */
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch =
        payment.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.supplierInvoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.reference.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesMethod =
        methodFilter === 'Tous' || payment.paymentMethod === methodFilter;

      const matchesStatus =
        statusFilter === 'Tous' || payment.status === statusFilter;

      return matchesSearch && matchesMethod && matchesStatus;
    });
  }, [payments, searchTerm, methodFilter, statusFilter]);

  /* ================= STATS ================= */
  const totalPayments = payments.length;
  const totalAmount = payments.reduce((s, p) => s + p.amount, 0);
  const validatedPayments = payments.filter(p => p.status === 'Validé').length;
  const pendingPayments = payments.filter(p => p.status === 'En Attente').length;

  /* ================= IMPRESSION RÈGLEMENT ================= */
  const handlePrintPayment = (payment: Payment) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir l'impression");
      return;
    }

    const html = `<!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>Règlement ${payment.reference}</title>
      <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; margin: 40px; background: #f9fafb; color: #333; }
        .container { max-width: 800px; margin: 0 auto; background: white; padding: 40px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
        .header { text-align: center; padding-bottom: 20px; border-bottom: 3px solid #2563eb; }
        .header h1 { color: #1e40af; margin: 0; font-size: 28px; }
        .header h2 { color: #1e3a8a; margin: 10px 0 0; font-size: 22px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin: 40px 0; }
        .info-block h3 { color: #1e40af; border-bottom: 1px solid #dbeafe; padding-bottom: 8px; margin-bottom: 15px; }
        .details { margin: 40px 0; padding: 20px; background: #f0f9ff; border-radius: 8px; border-left: 4px solid #2563eb; }
        .footer { margin-top: 80px; text-align: center; color: #6b7280; font-size: 12px; }
        @media print { body { background: white; margin: 0; } .container { box-shadow: none; padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>RÈGLEMENT CLIENT</h1>
          <h2>Référence : ${payment.reference || '—'}</h2>
        </div>

        <div class="info-grid">
          <div class="info-block">
            <h3>Client</h3>
            <div><strong>Raison sociale :</strong> ${payment.supplier}</div>
          </div>
          <div class="info-block">
            <h3>Facture liée</h3>
            <div><strong>Numéro facture :</strong> ${payment.invoiceNumber}</div>
            <div><strong>Bon de livraison :</strong> ${payment.supplierInvoiceNumber}</div>
          </div>
        </div>

        <div class="details">
          <div style="margin-bottom: 15px;"><strong>Montant réglé :</strong> ${formatCurrency(payment.amount)}</div>
          <div style="margin-bottom: 15px;"><strong>Mode de paiement :</strong> ${payment.paymentMethod}</div>
          <div style="margin-bottom: 15px;"><strong>Date du règlement :</strong> ${new Date(payment.date).toLocaleDateString('fr-FR')}</div>
          <div style="margin-bottom: 15px;"><strong>Statut :</strong> ${payment.status}</div>
        </div>

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')} 
          à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 800);
  };

  /* ================= RENDER ================= */
  return (
    <div className="space-y-6">

      {/* ================= STATS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard icon="ri-money-dollar-circle-line" color="blue" label="Total Règlements" value={totalPayments} />
        <StatCard icon="ri-check-double-line" color="green" label="Montant Total" value={formatCurrency(totalAmount)} />
        <StatCard icon="ri-shield-check-line" color="green" label="Validés" value={validatedPayments} />
        <StatCard icon="ri-time-line" color="yellow" label="En Attente" value={pendingPayments} />
      </div>

      {/* ================= FILTERS ================= */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <input
            type="text"
            placeholder="Rechercher par facture, fournisseur, référence..."
            className="flex-1 px-4 py-2 border rounded-md"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <select className="px-3 py-2 border rounded-md" value={methodFilter} onChange={e => setMethodFilter(e.target.value)}>
            <option value="Tous">Tous les modes</option>
            <option value="Virement">Virement</option>
            <option value="Chèque">Chèque</option>
            <option value="Espèces">Espèces</option>
            <option value="Carte">Carte</option>
            <option value="Mobile Money">Mobile Money</option>
            <option value="Compensation">Compensation</option>
          </select>

          <select className="px-3 py-2 border rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="Tous">Tous les statuts</option>
            <option value="Validé">Validé</option>
            <option value="En Attente">En Attente</option>
            <option value="Annulé">Annulé</option>
          </select>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <table className="min-w-full divide-y">
          <thead className="bg-gray-50">
            <tr>
              <Th>Règlement</Th>
              <Th>Facture</Th>
              <Th>Fournisseur</Th>
              <Th>Montant</Th>
              <Th>Mode</Th>
              <Th>Date</Th>
              <Th>Statut</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
                </td>
              </tr>
            ) : filteredPayments.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-gray-500">
                  Aucun règlement trouvé
                </td>
              </tr>
            ) : (
              filteredPayments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <Td>
                    <div className="font-medium">{p.reference}</div>
                    <div className="text-xs text-gray-500">ID: {p.id}</div>
                  </Td>
                  <Td>
                    <div className="font-medium">{p.invoiceNumber}</div>
                    <div className="text-xs text-gray-500">{p.supplierInvoiceNumber}</div>
                  </Td>
                  <Td>{p.supplier}</Td>
                  <Td className="font-medium">{formatCurrency(p.amount)}</Td>
                  <Td>
                    <i className={`${getMethodIcon(p.paymentMethod)} mr-2`} />
                    {p.paymentMethod}
                  </Td>
                  <Td>{new Date(p.date).toLocaleDateString('fr-FR')}</Td>
                  <Td>
                    <span className={`px-2 py-1 rounded-full border text-xs ${getStatusBadge(p.status)}`}>
                      {p.status}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setSelectedPayment(p);
                          setShowDetailModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Détail règlement"
                      >
                        <i className="ri-eye-line text-lg" />
                      </button>

                      <button
                        onClick={() => handlePrintPayment(p)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Imprimer règlement"
                      >
                        <i className="ri-printer-line text-lg" />
                      </button>

                      {p.status === 'En Attente' && (
                        <button
                          onClick={() => toast.info('Fonction annulation en cours de développement')}
                          className="text-red-600 hover:text-red-800"
                          title="Annuler règlement"
                        >
                          <i className="ri-close-circle-line text-lg" />
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ================= MODAL DÉTAIL RÈGLEMENT ================= */}
      {showDetailModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-gray-900">
                  Règlement {selectedPayment.reference}
                </h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl" />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold mb-4">Informations client</h4>
                  <div className="space-y-2">
                    <p><strong>Fournisseur :</strong> {selectedPayment.supplier}</p>
                    <p><strong>Facture :</strong> {selectedPayment.invoiceNumber}</p>
                    <p><strong>BL associé :</strong> {selectedPayment.supplierInvoiceNumber}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-4">Détails règlement</h4>
                  <div className="space-y-2">
                    <p><strong>Montant :</strong> {formatCurrency(selectedPayment.amount)}</p>
                    <p><strong>Mode :</strong> {selectedPayment.paymentMethod}</p>
                    <p><strong>Date :</strong> {new Date(selectedPayment.date).toLocaleDateString('fr-FR')}</p>
                    <p><strong>Référence :</strong> {selectedPayment.reference}</p>
                    <p><strong>Statut :</strong>
                      <span className={`ml-2 px-3 py-1 rounded-full text-xs ${getStatusBadge(selectedPayment.status)}`}>
                        {selectedPayment.status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-6 py-3 border rounded-lg hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                onClick={() => handlePrintPayment(selectedPayment)}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <i className="ri-printer-line mr-2" />
                Imprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= UI HELPERS ================= */
function StatCard({ icon, color, label, value }: { icon: string; color: string; label: string; value: string | number }) {
  const bgColor = color === 'blue' ? 'bg-blue-100' : color === 'green' ? 'bg-green-100' : 'bg-yellow-100';
  const textColor = color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : 'text-yellow-600';

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border flex items-center">
      <div className={`p-3 rounded-full ${bgColor}`}>
        <i className={`${icon} ${textColor} text-xl`} />
      </div>
      <div className="ml-4">
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${className}`}>{children}</th>;
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-6 py-4 text-sm ${className}`}>{children}</td>;
}